import { get, post, put } from '../utils/networking';
import type { WorkEnvironmentSurveyStatus } from './workEnvironmentTypes';

export type HmsNotificationType =
  | 'SURVEY_STARTED'
  | 'RISK_CALCULATED'
  | 'INSUFFICIENT_RESPONSES'
  | 'ONE_REPORT'
  | 'PLAN_REVIEW'
  | 'DOCUMENTATION_TO_SIGN'
  | 'EVALUATION_DONE'
  | 'VERSION_TO_APPROVE'
  | 'DECISION_OVERDUE'
  | 'MEASURE_OVERDUE'
  | 'ASSESSED_WITHOUT_SAFETY_REP'
  | 'PLAN_APPROVED_WITHOUT_SAFETY_REP'
  | 'RISK_REVIEW_REMINDER'
  | 'PLAN_REVIEW_REMINDER'
  | 'VERSION_APPROVED_BY_HR'
  | 'DOCUMENTATION_DEVIATION'
  | 'SAFETY_REP_ROLE';

/** A task is read when it is done, a serious report when it is seen, information when they like */
export type HmsNotificationKind = 'task' | 'serious' | 'info';

export type HmsNotification = {
  id: number;
  type: HmsNotificationType;
  kind: HmsNotificationKind;
  createdAt: string;
  readAt: string | null;
  survey: {
    id: number;
    title: string;
    team: { id: number; name: string };
  } | null;
  /** «Survey started» about the evaluation of the survey, as the email says. Missing from older APIs. */
  aboutEvaluation?: boolean;
};

export type SafetyRepTaskKind =
  | 'sign'
  | 'approveVersion'
  | 'reviewPlan'
  | 'highRisk'
  | 'assessRisk';

export type SafetyRepTask = {
  kind: SafetyRepTaskKind;
  /** Empty for a version of the HMS setup, which belongs to HR */
  surveyId: number | null;
  teamId: number | null;
  team: string | null;
  areas: string[];
  version: {
    subject: 'riskMatrix' | 'template';
    id: number;
    version: number;
  } | null;
  since: string;
  /** HR's deadline, when the task has one */
  due: string | null;
};

export type SafetyRepTeam = {
  id: number;
  name: string;
  /** The user leads the team, so they cannot act as its safety representative. Missing from older backends. */
  leads?: boolean;
  size: number;
  leaders: string[];
  survey: {
    id: number;
    title: string;
    status: WorkEnvironmentSurveyStatus;
    createdAt: string;
    /** Unknown until the risk is calculated */
    highRisk: number | null;
    /** While the team is answering */
    collecting: {
      responses: number;
      expected: number;
      deadline: string | null;
    } | null;
  } | null;
};

export type CompletedKind =
  | 'documentation'
  | 'riskMatrix'
  | 'template'
  | 'plan';

export type CompletedOutcome = 'signed' | 'approved' | 'returned' | 'commented';

export type TimelineEventKind =
  | 'sent'
  | 'notified'
  | 'returned'
  | 'commented'
  | 'signed'
  | 'approved'
  | 'planApproved';

export type CompletedTask = {
  key: string;
  kind: CompletedKind;
  outcome: CompletedOutcome;
  team: string | null;
  /** The areas of an action plan */
  areas: string[];
  version: number | null;
  surveyId: number | null;
  at: string;
  comment: string | null;
  events: Array<{ kind: TimelineEventKind; at: string }>;
};

export type SignatureStatus = 'REQUESTED' | 'SIGNED' | 'RETURNED' | 'DEVIATION' | 'WITHDRAWN';

export type DocumentationSignature = {
  surveyId: number;
  status: SignatureStatus;
  requestedAt: string;
  requestedBy: string | null;
  respondedAt: string | null;
  respondedBy: string | null;
  comment: string | null;
};

export type SafetyRepWork = {
  organizationName: string;
  isMain: boolean;
  tasks: SafetyRepTask[];
  teams: SafetyRepTeam[];
  /** Waiting are the tasks not done yet and the serious reports not seen */
  notifications: { unread: number; waiting: number; latest: HmsNotification[] };
  preferences: {
    types: Array<{ type: HmsNotificationType; enabled: boolean }>;
    email: boolean;
  };
  documentation: Array<
    DocumentationSignature & { title: string; team: string; canUndo: boolean }
  >;
  /** What the safety representative has done before, newest first */
  completed: CompletedTask[];
};

export type NotificationPreferences = SafetyRepWork['preferences'];

const PATH = '/api/hms/safety-rep';

export const getSafetyRepWork = (language: string) =>
  get<SafetyRepWork>(`${PATH}/work?language=${encodeURIComponent(language)}`);

export const getSafetyRepNotifications = () =>
  get<HmsNotification[]>(`${PATH}/notifications`);

export const getSafetyRepUnread = () =>
  get<{ unread: number; waiting: number }>(`${PATH}/notifications/unread`);

/** One notification, or all of them */
export const markNotificationsRead = (id: number | null) =>
  post<void>(`${PATH}/notifications/read`, { id });

export const saveNotificationPreferences = (
  preferences: NotificationPreferences,
) => put<void>(`${PATH}/notifications/preferences`, preferences);

export const signDocumentation = (surveyId: number, comment: string) =>
  post<void>(`${PATH}/documentation/${surveyId}/sign`, {
    confirmed: true,
    comment,
  });

export const returnDocumentation = (surveyId: number, comment: string) =>
  post<void>(`${PATH}/documentation/${surveyId}/return`, { comment });

export const undoDocumentationAnswer = (surveyId: number) =>
  post<void>(`${PATH}/documentation/${surveyId}/undo`, {});
